import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../services/api";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import InquiryListPane from "../features/inquiries/InquiryListPane";
import InquiryCenterPane from "../features/inquiries/InquiryCenterPane";
import { OriginalEmailModal, PromptTemplatesModal, RatingDetailModal, ReplyPreviewModal, SendConfirm } from "../features/inquiries/InquiryModals";

const EMPTY_FILTERS = { q: "", status: "", rating: "", start_date: "", end_date: "" };
const DETAIL_CACHE_TTL = 30_000;
const CONTEXT_CACHE_TTL = 60_000;
const SECONDARY_CACHE_TTL = 60_000;
const SILENT_LIST_REFRESH_INTERVAL = 20_000;
const SILENT_LIST_REFRESH_THROTTLE = 5_000;
const detailCache = new Map();
const contextCache = new Map();
const detailRequests = new Map();
const contextRequests = new Map();
const secondaryCache = new Map();
const secondaryRequests = new Map();
const bodyHtmlRequests = new Map();

function performanceMark(name) {
  if (typeof performance === "undefined") return;
  performance.mark(`bluesun:${name}`);
}

function performanceMeasure(name, start, end) {
  if (typeof performance === "undefined") return;
  try {
    const measure = performance.measure(
      `bluesun:${name}`,
      `bluesun:${start}`,
      `bluesun:${end}`,
    );
    if (import.meta.env.DEV || localStorage.getItem("bluesun:performance-debug") === "1") {
      console.debug(`[performance] ${name}: ${measure.duration.toFixed(1)}ms`);
    }
  } catch { /* a navigation can legitimately remove an earlier mark */ }
}

function inquiryIdFromHash() {
  const match = window.location.hash.match(/^#\/?inquiries\/(\d+)(?:[/?]|$)/);
  const value = Number(match?.[1]);
  return Number.isFinite(value) && value > 0 ? value : null;
}

function consumeRequestedInquiryId() {
  const value = inquiryIdFromHash() || Number(sessionStorage.getItem("bluesun:selected-inquiry"));
  sessionStorage.removeItem("bluesun:selected-inquiry");
  return Number.isFinite(value) && value > 0 ? value : null;
}

function cachedValue(cache, id, ttl) {
  const entry = cache.get(Number(id));
  if (!entry || Date.now() - entry.savedAt > ttl) return null;
  return entry.value;
}

function loadCachedResource(cache, pending, id, loader, { force = false, ttl: explicitTtl } = {}) {
  const key = Number(id);
  const ttl = explicitTtl || (cache === detailCache ? DETAIL_CACHE_TTL : CONTEXT_CACHE_TTL);
  const cached = !force ? cachedValue(cache, key, ttl) : null;
  if (cached) return Promise.resolve(cached);
  if (pending.has(key)) return pending.get(key);
  const request = loader()
    .then((value) => {
      cache.set(key, { value, savedAt: Date.now() });
      return value;
    })
    .finally(() => pending.delete(key));
  pending.set(key, request);
  return request;
}

function loadDeferredBodyHtml(id) {
  const key = Number(id);
  if (bodyHtmlRequests.has(key)) return bodyHtmlRequests.get(key);
  const request = api.inquiryBodyHtml(key)
    .finally(() => bodyHtmlRequests.delete(key));
  bodyHtmlRequests.set(key, request);
  return request;
}

function hasCompleteDetailContract(value) {
  return Boolean(
    value
    && !value._summary
    && Object.prototype.hasOwnProperty.call(value, "body_html_deferred"),
  );
}

function shouldLoadDeferredBody(value) {
  if (!value?.id || value._summary || value.body_html) return false;
  const hasText = Boolean(String(value.body_full_text || value.body || "").trim());
  return Boolean(value.body_html_deferred || !hasText);
}

export default function InquiryPage({ active = true, notify, onDashboardChanged }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const debouncedQuery = useDebouncedValue(filters.q, 350);
  const [page, setPage] = useState(1);
  const [list, setList] = useState({ items: [], total: 0 });
  const [listState, setListState] = useState({ loading: true, error: "" });
  const requestedSelectionRef = useRef(null);
  const firstListItemIdRef = useRef(null);
  const [selectedId, setSelectedId] = useState(() => {
    const requestedId = consumeRequestedInquiryId();
    requestedSelectionRef.current = requestedId;
    return requestedId;
  });
  const [inquiry, setInquiry] = useState(null);
  const [context, setContext] = useState(null);
  const [secondary, setSecondary] = useState(null);
  const [detailState, setDetailState] = useState({ loading: false, error: "" });
  const [bodyContentState, setBodyContentState] = useState({ id: null, loading: false, error: "" });
  const [bodyRetryKey, setBodyRetryKey] = useState(0);
  const [secondaryState, setSecondaryState] = useState({ loading: false, error: "" });
  const [settings, setSettings] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  const [busy, setBusy] = useState("");
  const [aiProgress, setAiProgress] = useState(null);
  const [contextBusy, setContextBusy] = useState(false);
  const [modals, setModals] = useState({ original: false, prompts: false, preview: false, send: false });
  const [sendPayload, setSendPayload] = useState(null);
  const [ratingEditor, setRatingEditor] = useState({ open: false, target: null, selectedRating: 0 });
  const [detailTab, setDetailTab] = useState("mail");
  const [mobileView, setMobileView] = useState("list");
  const [listWidth, setListWidth] = useState(
    () => Number(localStorage.getItem("bluesun:inquiry-list-width")) || 360,
  );
  const [resizing, setResizing] = useState(false);
  const workspaceRef = useRef(null);
  const resizeState = useRef(null);
  const detailRequestSequence = useRef(0);
  const contextRequestSequence = useRef(0);
  const secondaryRequestSequence = useRef(0);
  const listRequestSequence = useRef(0);
  const listRequestPendingRef = useRef(false);
  const listRequestStartedAtRef = useRef(0);
  const listItemsRef = useRef([]);
  const hasBootstrappedRef = useRef(false);
  const hoverPrefetchTimerRef = useRef(null);
  const hoverPrefetchActiveRef = useRef(false);
  const sendTransitionRef = useRef({ id: null, status: "" });
  const [selectedRows, setSelectedRows] = useState(() => new Set());
  const pageSize = 50;
  const clampListWidth = useCallback((width) => {
    const workspaceWidth = workspaceRef.current?.getBoundingClientRect().width || window.innerWidth;
    return Math.max(280, Math.min(width, Math.min(620, workspaceWidth - 520)));
  }, []);

  const loadList = useCallback(async (options = {}) => {
    const sequence = ++listRequestSequence.current;
    const background = Boolean(options.background);
    listRequestPendingRef.current = true;
    listRequestStartedAtRef.current = Date.now();
    if (!background) setListState({ loading: true, error: "" });
    try {
      const includeFirstDetail = !background
        && !hasBootstrappedRef.current
        && !requestedSelectionRef.current;
      const result = await api.inquiries({
        q: debouncedQuery,
        status: filters.status,
        rating: filters.rating,
        date_from: filters.start_date,
        date_to: filters.end_date,
        limit: pageSize,
        offset: (page - 1) * pageSize,
        include_total: background ? 0 : 1,
        include_first_detail: includeFirstDetail ? 1 : 0,
      });
      if (sequence !== listRequestSequence.current) return;
      setList((current) => ({
        ...result,
        total: result.total === null || result.total === undefined ? current.total : result.total,
      }));
      const items = result.items || [];
      listItemsRef.current = items;
      const firstId = items[0]?.id || null;
      firstListItemIdRef.current = firstId;
      if (result.first_detail) {
        const firstDetail = result.first_detail;
        detailCache.set(Number(firstDetail.id), {
          value: firstDetail,
          savedAt: Date.now(),
          updatedAt: firstDetail.updated_at || "",
        });
        setInquiry(firstDetail);
        performanceMark("first_detail_shell_visible");
        performanceMeasure(
          "workspace_to_first_detail_shell",
          "inquiry_workspace_open",
          "first_detail_shell_visible",
        );
        performanceMark("first_detail_body_visible");
        performanceMeasure(
          "workspace_to_first_detail",
          "inquiry_workspace_open",
          "first_detail_body_visible",
        );
      }
      setSelectedId((current) => {
        const requestedId = requestedSelectionRef.current;
        if (requestedId && Number(current) === Number(requestedId)) return current;
        if (options.preserveSelection && current) return current;
        return items.some((item) => Number(item.id) === Number(current)) ? current : firstId;
      });
      hasBootstrappedRef.current = true;
      performanceMark("list_visible");
      performanceMeasure("workspace_to_list", "inquiry_workspace_open", "list_visible");
      if (!options.preserveSelection) setSelectedRows(new Set());
    } catch (error) {
      if (sequence === listRequestSequence.current && !background) {
        setListState({ loading: false, error: error.message });
      }
      return;
    } finally {
      if (sequence === listRequestSequence.current) listRequestPendingRef.current = false;
    }
    if (sequence === listRequestSequence.current) {
      setListState((current) => ({ ...current, loading: false, error: "" }));
    }
  }, [debouncedQuery, filters.status, filters.rating, filters.start_date, filters.end_date, page]);

  const loadContext = useCallback(async (id, { force = false } = {}) => {
    if (!id) { setContext(null); return; }
    const sequence = ++contextRequestSequence.current;
    const cached = !force ? cachedValue(contextCache, id, CONTEXT_CACHE_TTL) : null;
    if (cached) setContext(cached);
    try {
      const customer = await loadCachedResource(
        contextCache,
        contextRequests,
        id,
        () => api.customerContext(id),
        { force },
      );
      if (sequence === contextRequestSequence.current) setContext(customer);
    } catch {
      if (sequence === contextRequestSequence.current && !cached) setContext(null);
    }
  }, []);

  const loadSecondary = useCallback(async (id, { force = false } = {}) => {
    const sequence = ++secondaryRequestSequence.current;
    if (!id) { setSecondary(null); return; }
    const cached = !force ? cachedValue(secondaryCache, id, SECONDARY_CACHE_TTL) : null;
    if (cached) {
      if (sequence === secondaryRequestSequence.current) {
        setSecondary(cached);
        setSecondaryState({ loading: false, error: "" });
      }
      return;
    }
    setSecondaryState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const value = await loadCachedResource(
        secondaryCache,
        secondaryRequests,
        id,
        () => api.inquiryResearchDetail(id),
        { force, ttl: SECONDARY_CACHE_TTL },
      );
      if (sequence === secondaryRequestSequence.current) {
        setSecondary(value);
        setSecondaryState({ loading: false, error: "" });
      }
    } catch (error) {
      if (sequence === secondaryRequestSequence.current) {
        setSecondaryState({ loading: false, error: error.message || "二级数据加载失败" });
      }
    }
  }, []);

  const loadDetail = useCallback(async (id = selectedId, { force = false } = {}) => {
    if (!id) { setInquiry(null); setContext(null); return; }
    const sequence = ++detailRequestSequence.current;
    const entry = detailCache.get(Number(id));
    const retainedDetail = entry?.value;
    const listUpdatedAt = listItemsRef.current.find(
      (item) => Number(item.id) === Number(id),
    )?.updated_at;
    const cacheMatchesVersion = Boolean(
      entry?.value
      && listUpdatedAt
      && entry.value.updated_at
      && String(listUpdatedAt) === String(entry.value.updated_at),
    );
    const cacheIsFresh = Boolean(
      entry?.value
      && hasCompleteDetailContract(entry.value)
      && (Date.now() - entry.savedAt <= DETAIL_CACHE_TTL || cacheMatchesVersion),
    );
    if (entry?.value) {
      setInquiry(entry.value);
      setDetailState({ loading: false, error: "" });
    } else {
      setInquiry((current) => Number(current?.id) === Number(id) ? current : null);
      setDetailState({ loading: true, error: "" });
    }
    if (!force && cacheIsFresh) return;
    try {
      performanceMark("detail_core_request_started");
      const detail = await loadCachedResource(
        detailCache,
        detailRequests,
        id,
        () => api.inquiry(id, true),
        { force },
      );
      if (sequence !== detailRequestSequence.current) return;
      const retainedBodyHtml = String(retainedDetail?.body_html || "");
      const retainedBodyText = String(retainedDetail?.body_full_text || "");
      const hydratedDetail = retainedBodyHtml || retainedBodyText
        ? {
          ...detail,
          body_full_text: retainedBodyText || detail.body_full_text || "",
          body_html: retainedBodyHtml || detail.body_html || "",
          body_html_deferred: false,
        }
        : detail;
      detailCache.set(Number(id), { value: hydratedDetail, savedAt: Date.now() });
      setInquiry(hydratedDetail);
      if (Number(requestedSelectionRef.current) === Number(id)) requestedSelectionRef.current = null;
      setDetailState({ loading: false, error: "" });
      performanceMark("detail_core_visible");
      performanceMeasure("inquiry_click_to_core", "inquiry_click", "detail_core_visible");
    } catch (error) {
      if (sequence !== detailRequestSequence.current) return;
      const requestedId = requestedSelectionRef.current;
      const shouldFallback = Number(requestedId) === Number(id)
        || /询盘不存在|not found/i.test(error.message || "");
      if (shouldFallback) {
        requestedSelectionRef.current = null;
        const fallbackId = firstListItemIdRef.current;
        setInquiry(null);
        setSelectedId(fallbackId && Number(fallbackId) !== Number(id) ? fallbackId : null);
        setDetailState(fallbackId && Number(fallbackId) !== Number(id)
          ? { loading: true, error: "" }
          : { loading: false, error: fallbackId ? error.message : "" });
        return;
      }
      setDetailState({ loading: false, error: error.message });
    }
  }, [selectedId]);

  useEffect(() => {
    if (active) performanceMark("inquiry_workspace_open");
  }, [active]);
  useEffect(() => {
    const selectRequestedInquiry = () => {
      const requestedId = consumeRequestedInquiryId();
      if (!requestedId) return;
      requestedSelectionRef.current = requestedId;
      setFilters(EMPTY_FILTERS);
      setPage(1);
      setSelectedId(requestedId);
      setDetailTab("mail");
      setMobileView("center");
    };
    window.addEventListener("hashchange", selectRequestedInquiry);
    selectRequestedInquiry();
    return () => window.removeEventListener("hashchange", selectRequestedInquiry);
  }, []);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadDetail(); }, [loadDetail]);
  useEffect(() => {
    if (!active || !inquiry?.id || inquiry._summary || detailTab !== "context") return;
    loadContext(inquiry.id);
  }, [active, inquiry?.id, inquiry?._summary, detailTab, loadContext]);
  useEffect(() => {
    if (!active || !inquiry?.id || inquiry._summary || !["analysis", "reply"].includes(detailTab)) return;
    loadSecondary(inquiry.id);
  }, [active, inquiry?.id, inquiry?._summary, detailTab, loadSecondary]);
  // The rating modal shows the live seven-step coverage, which comes from the
  // research detail; load it even when opened from the overview tab.
  useEffect(() => {
    if (!active || !ratingEditor.open || !inquiry?.id || inquiry._summary) return;
    loadSecondary(inquiry.id);
  }, [active, ratingEditor.open, inquiry?.id, inquiry?._summary, loadSecondary]);
  useEffect(() => {
    if (!active) return undefined;
    const refreshSilently = () => {
      // Background tabs do not poll; visibilitychange refreshes on return.
      if (document.hidden) return;
      const recentlyStarted = Date.now() - listRequestStartedAtRef.current < SILENT_LIST_REFRESH_THROTTLE;
      if (listRequestPendingRef.current || recentlyStarted) return;
      loadList({ preserveSelection: true, background: true });
    };
    refreshSilently();
    const timer = window.setInterval(refreshSilently, SILENT_LIST_REFRESH_INTERVAL);
    const handleVisibility = () => {
      if (document.visibilityState === "visible") refreshSilently();
    };
    window.addEventListener("focus", refreshSilently);
    window.addEventListener("online", refreshSilently);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refreshSilently);
      window.removeEventListener("online", refreshSilently);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [active, loadList]);
  useEffect(() => {
    if (detailTab !== "mail" || !shouldLoadDeferredBody(inquiry)) return undefined;
    const inquiryId = Number(inquiry.id);
    let alive = true;
    setBodyContentState({ id: inquiryId, loading: true, error: "" });
    loadDeferredBodyHtml(inquiryId)
      .then((payload) => {
        const cached = detailCache.get(inquiryId)?.value;
        if (cached) {
          detailCache.set(inquiryId, {
            value: {
              ...cached,
              body_full_text: payload.body_text || cached.body_full_text || "",
              body_html: payload.body_html || "",
              body_html_deferred: false,
            },
            savedAt: Date.now(),
          });
        }
        if (alive) {
          setInquiry((current) => Number(current?.id) === inquiryId
            ? {
              ...current,
              body_full_text: payload.body_text || current.body_full_text || "",
              body_html: payload.body_html || "",
              body_html_deferred: false,
            }
            : current);
          setBodyContentState({ id: inquiryId, loading: false, error: "" });
        }
      })
      .catch((error) => {
        if (alive) {
          setBodyContentState({
            id: inquiryId,
            loading: false,
            error: error?.message || "完整正文读取失败，请重试。",
          });
        }
      });
    return () => {
      alive = false;
    };
  }, [bodyRetryKey, detailTab, inquiry]);
  useEffect(() => {
    Promise.all([api.settings(), api.ccSuggestions()])
      .then(([nextSettings, nextSuggestions]) => {
        setSettings(nextSettings);
        setSuggestions(nextSuggestions.items || nextSuggestions || []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!active || !inquiry?.id || !["send_queued", "sending"].includes(inquiry.status)) return undefined;
    const timer = window.setInterval(() => {
      loadDetail(inquiry.id, { force: true });
    }, 6000);
    return () => window.clearInterval(timer);
  }, [active, inquiry?.id, inquiry?.status, loadDetail]);
  useEffect(() => {
    const next = { id: Number(inquiry?.id || 0), status: String(inquiry?.status || "") };
    const previous = sendTransitionRef.current;
    const wasSending = previous.id === next.id && ["send_queued", "sending"].includes(previous.status);
    if (wasSending && next.status === "replied") {
      notify("邮件已发送", "success", "发件邮箱服务器已接受邮件。", 5000);
    } else if (wasSending && next.status === "failed") {
      notify("邮件发送失败", "danger", inquiry?.last_error || "请检查邮箱配置后重新发送。", 8000);
    }
    sendTransitionRef.current = next;
  }, [inquiry?.id, inquiry?.status, inquiry?.last_error, notify]);
  useEffect(() => {
    if (!active || !inquiry?.id) return undefined;
    const processing = ["ai_queued", "translating", "researching", "generating_reply"].includes(inquiry.status);
    const manuallyProcessing = ["queue-ai", "translate", "research", "generate"].includes(busy);
    if (!processing && !manuallyProcessing) {
      setAiProgress(null);
      return undefined;
    }
    const source = new EventSource(`/api/inquiries/${inquiry.id}/events`);
    let fallbackStartTimer = null;
    let fallbackPollingTimer = null;
    const stopFallbackPolling = () => {
      if (fallbackStartTimer) window.clearTimeout(fallbackStartTimer);
      if (fallbackPollingTimer) window.clearInterval(fallbackPollingTimer);
      fallbackStartTimer = null;
      fallbackPollingTimer = null;
    };
    const startFallbackPolling = () => {
      if (fallbackStartTimer || fallbackPollingTimer) return;
      fallbackStartTimer = window.setTimeout(() => {
        fallbackStartTimer = null;
        fallbackPollingTimer = window.setInterval(() => {
          loadDetail(inquiry.id, { force: true });
        }, 10_000);
      }, 12_000);
    };
    const eventNames = [
      "processing_started", "translation_started", "translation_completed",
      "research_started", "search_started", "search_results_ready",
      "source_fetching", "source_fetch_completed", "search_completed",
      "reply_started", "reply_completed", "content_delta", "completed", "failed",
    ];
    const handleEvent = (event) => {
      stopFallbackPolling();
      let payload;
      try { payload = JSON.parse(event.data); } catch { return; }
      setAiProgress((current) => {
        const partial = { ...(current?.partial || {}) };
        if (payload.event === "content_delta" && payload.channel && payload.delta) {
          partial[payload.channel] = `${partial[payload.channel] || ""}${payload.delta}`.slice(-5000);
        }
        return { ...(current || {}), ...payload, partial };
      });
      if (payload.event === "completed" || payload.event === "failed") {
        source.close();
        secondaryCache.delete(Number(inquiry.id));
        loadDetail(inquiry.id, { force: true });
        loadList({ preserveSelection: true, background: true });
      }
    };
    eventNames.forEach((name) => source.addEventListener(name, handleEvent));
    source.onerror = () => {
      if (source.readyState === EventSource.CLOSED) return;
      startFallbackPolling();
      setAiProgress((current) => current || {
        event: "connecting",
        elapsed_seconds: 0,
        partial: {},
      });
    };
    return () => {
      stopFallbackPolling();
      eventNames.forEach((name) => source.removeEventListener(name, handleEvent));
      source.close();
    };
  }, [active, inquiry?.id, inquiry?.status, busy, loadDetail, loadList]);
  useEffect(() => {
    if (!resizing) return undefined;
    const move = (event) => {
      if (!resizeState.current) return;
      setListWidth(clampListWidth(
        resizeState.current.startWidth + event.clientX - resizeState.current.startX,
      ));
    };
    const end = (event) => {
      if (!resizeState.current) return;
      const finalWidth = clampListWidth(
        resizeState.current.startWidth + event.clientX - resizeState.current.startX,
      );
      setListWidth(finalWidth);
      localStorage.setItem("bluesun:inquiry-list-width", String(finalWidth));
      resizeState.current = null;
      setResizing(false);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", end, { once: true });
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", end);
    };
  }, [resizing, clampListWidth]);

  const updateSelected = (updated) => {
    detailCache.set(Number(updated.id), {
      value: updated,
      savedAt: Date.now(),
      updatedAt: updated.updated_at || "",
    });
    setInquiry(updated);
    setList((current) => ({ ...current, items: current.items.map((item) => Number(item.id) === Number(updated.id) ? { ...item, ...updated } : item) }));
  };
  const run = async (key, action, success, after) => {
    setBusy(key);
    try {
      const result = await action();
      if (result?.id) updateSelected(result);
      if (after) await after(result);
      notify(success, "success");
      onDashboardChanged?.();
      loadList({ preserveSelection: true });
      return result;
    } catch (error) {
      notify("操作失败", "danger", error.message);
      throw error;
    } finally { setBusy(""); }
  };
  const handleFilters = (patch) => { setFilters((value) => ({ ...value, ...patch })); setPage(1); };
  const handleSelect = (item) => {
    performanceMark("inquiry_click");
    requestedSelectionRef.current = null;
    secondaryRequestSequence.current += 1;
    const cached = detailCache.get(Number(item.id))?.value;
    setInquiry(cached || { ...item, _summary: true });
    setContext(cachedValue(contextCache, item.id, CONTEXT_CACHE_TTL));
    setSecondary(cachedValue(secondaryCache, item.id, SECONDARY_CACHE_TTL));
    setSecondaryState({ loading: false, error: "" });
    setSelectedId(item.id);
    setMobileView("center");
    setDetailTab("mail");
    performanceMark("detail_shell_visible");
    performanceMeasure("inquiry_click_to_shell", "inquiry_click", "detail_shell_visible");
    if (cached) {
      performanceMark("detail_core_visible");
      performanceMeasure("inquiry_click_to_core", "inquiry_click", "detail_core_visible");
    }
  };
  const scheduleHoverPrefetch = useCallback((item) => {
    if (!item?.id || detailCache.get(Number(item.id)) || hoverPrefetchActiveRef.current) return;
    if (hoverPrefetchTimerRef.current) window.clearTimeout(hoverPrefetchTimerRef.current);
    hoverPrefetchTimerRef.current = window.setTimeout(() => {
      hoverPrefetchTimerRef.current = null;
      hoverPrefetchActiveRef.current = true;
      loadCachedResource(
        detailCache,
        detailRequests,
        item.id,
        () => api.inquiry(item.id, true),
      ).catch(() => {}).finally(() => { hoverPrefetchActiveRef.current = false; });
    }, 220);
  }, []);
  const cancelHoverPrefetch = useCallback(() => {
    if (hoverPrefetchTimerRef.current) {
      window.clearTimeout(hoverPrefetchTimerRef.current);
      hoverPrefetchTimerRef.current = null;
    }
  }, []);
  const handleOpenHistory = useCallback((id) => {
    const targetId = Number(id);
    if (!Number.isFinite(targetId) || targetId <= 0) {
      notify("无法打开历史询盘", "danger", "历史询盘编号无效，请刷新后重试。");
      return;
    }
    requestedSelectionRef.current = null;
    secondaryRequestSequence.current += 1;
    setInquiry(detailCache.get(targetId)?.value || null);
    setContext(cachedValue(contextCache, targetId, CONTEXT_CACHE_TTL));
    setSecondary(cachedValue(secondaryCache, targetId, SECONDARY_CACHE_TTL));
    setSelectedId(targetId);
    setMobileView("center");
    setDetailTab("mail");
  }, [notify]);
  const handleRating = (target, rating) => {
    if (!target?.id) return;
    const detailedTarget = Number(target.id) === Number(inquiry?.id) ? inquiry : target;
    setRatingEditor({ open: true, target: detailedTarget, selectedRating: rating });
  };
  const saveRating = async (rating, reason) => {
    const target = ratingEditor.target;
    if (!target?.id) return;
    setBusy("rating");
    try {
      const result = await api.updateBusinessValue(target.id, rating, reason);
      if (Number(target.id) === Number(inquiry?.id)) updateSelected(result);
      // The same email address can own several independent projects. Never
      // copy one project's manual rating to every inquiry from that address.
      setList((current) => ({
        ...current,
        items: current.items.map((item) => Number(item.id) === Number(target.id)
          ? {
            ...item,
            business_value_rating: result.business_value_rating,
            system_business_value_rating: result.system_business_value_rating,
            manual_business_value_rating: result.manual_business_value_rating,
            rating_total_score: result.rating_total_score,
            rating_status: result.rating_status,
            verification_level: result.verification_level,
            sop_segment: result.sop_segment,
            research_priority: result.research_priority,
            sop_segment_reason: result.sop_segment_reason,
            email_verification_status: result.email_verification_status,
            phone_verification_status: result.phone_verification_status,
            price_protection: result.price_protection,
          }
          : item),
      }));
      await Promise.all([
        loadList({ preserveSelection: true, background: true }),
        inquiry?.id ? loadDetail(inquiry.id, { force: true }) : Promise.resolve(),
        inquiry?.id ? loadContext(inquiry.id, { force: true }) : Promise.resolve(),
      ]);
      setRatingEditor({ open: false, target: null, selectedRating: 0 });
      notify("星级调整已保存", "success", "系统评分仍被保留，可随时恢复。");
    } catch (error) { notify("星级更新失败", "danger", error.message); } finally { setBusy(""); }
  };
  const restoreSystemRating = async () => {
    const target = ratingEditor.target;
    if (!target?.id) return;
    setBusy("rating");
    try {
      const result = await api.updateBusinessValue(target.id, 0, "恢复系统评分");
      if (Number(target.id) === Number(inquiry?.id)) updateSelected(result);
      await loadList({ preserveSelection: true, background: true });
      setRatingEditor({ open: false, target: null, selectedRating: 0 });
      notify("已恢复系统评分", "success");
    } catch (error) { notify("恢复失败", "danger", error.message); } finally { setBusy(""); }
  };
  const handleSync = () => run("sync", api.sync, "邮箱同步任务已启动");
  const handleRetry = (target) => run("retry", () => api.retryInquiry(target.id), "已加入重新处理队列");
  const handleAutoProcess = (target = inquiry) => run("queue-ai", () => api.queueInquiryAi(target.id), "已加入 AI 自动处理队列");
  const handleTranslate = () => run("translate", () => api.translateInquiry(inquiry.id), "中文翻译已生成", () => loadDetail(inquiry.id));
  const handleBulkProcess = async () => {
    const targets = list.items.filter((item) => selectedRows.has(Number(item.id)));
    if (!targets.length) return;
    setBusy("bulk");
    const results = [];
    for (const target of targets) {
      try {
        const result = target.status === "failed"
          ? await api.retryInquiry(target.id)
          : await api.queueInquiryAi(target.id);
        results.push({ ok: true, result });
      } catch (error) {
        results.push({ ok: false, error });
      }
    }
    const failed = results.filter((item) => !item.ok).length;
    notify(
      failed ? `已提交 ${results.length - failed} 封，${failed} 封失败` : `已提交 ${results.length} 封询盘`,
      failed ? "warning" : "success",
      failed ? "失败项可在列表中单封重试。" : "后台将按并发配置自动处理。",
    );
    setBusy("");
    setSelectedRows(new Set());
    loadList({ preserveSelection: true });
  };
  const handleResearch = () => run("research", () => api.research(inquiry.id), "AI 背调已生成", (result) => {
    updateSelected(result);
    secondaryCache.delete(Number(result.id));
    setSecondary(null);
    setDetailTab("analysis");
  });
  const handleGenerate = (instruction = "") => run("generate", () => api.generate(inquiry.id, instruction.trim() ? { revision_instruction: instruction.trim() } : null), "英文回复草稿已生成", (result) => { updateSelected(result); setDetailTab("reply"); });
  const handleSaveDraft = (draft) => run("save", () => api.saveDraft(inquiry.id, draft), "草稿已保存");
  const handleRecipients = async (emails) => { const updated = await api.saveInquiryRecipients(inquiry.id, emails); updateSelected(updated); };
  const handleContext = async (action, message) => {
    setContextBusy(true);
    try {
      await action();
      await loadContext(inquiry.id, { force: true });
      notify(message, "success");
    } catch (error) { notify("保存失败", "danger", error.message); } finally { setContextBusy(false); }
  };
  const handleOpenSend = (payload) => { setSendPayload(payload); setModals((value) => ({ ...value, send: true })); };
  const handlePreview = (payload) => { setSendPayload(payload); setModals((value) => ({ ...value, preview: true })); };
  const confirmSend = () => run("send", () => api.send(inquiry.id, {
    ai_draft: sendPayload.draft,
    recipient_email: sendPayload.recipients[0],
    recipient_emails: sendPayload.recipients,
    cc_emails: sendPayload.cc,
  }), "已提交发送，您可以继续处理其他询盘", () => setModals((value) => ({ ...value, send: false })));
  const resizeListBy = (delta) => {
    setListWidth((current) => {
      const next = clampListWidth(current + delta);
      localStorage.setItem("bluesun:inquiry-list-width", String(next));
      return next;
    });
  };
  const startResize = (event) => {
    if (window.innerWidth <= 960) return;
    event.preventDefault();
    resizeState.current = { startX: event.clientX, startWidth: listWidth };
    setResizing(true);
  };
  const displayedInquiry = inquiry && secondary && Number(inquiry.id) === Number(selectedId)
    ? {
        ...inquiry,
        research_sources: secondary.research_sources || secondary.sources || [],
        reply_knowledge_evidence: secondary.reply_knowledge_evidence || [],
        rating_history: secondary.rating_history || [],
        sop_compatibility: secondary.sop_compatibility || inquiry.sop_compatibility,
        sop_segment: secondary.sop_compatibility?.segment || inquiry.sop_segment,
        verification_level: secondary.verification_level || inquiry.verification_level,
        rating_snapshot: { ...inquiry.rating_snapshot, verification_evidence: secondary.verification_evidence || inquiry.rating_snapshot?.verification_evidence, risk_flags: secondary.risk_flags ?? inquiry.rating_snapshot?.risk_flags },
      }
    : inquiry;

  return (
    <div
      ref={workspaceRef}
      className={`inquiry-workspace mobile-view-${mobileView} ${resizing ? "is-resizing" : ""}`}
      style={{
        "--detail-font-size": `${settings.inquiry_detail_font_size || 15}px`,
        "--inquiry-list-width": `${listWidth}px`,
      }}
    >
      <InquiryListPane
        {...list}
        page={page}
        pageSize={pageSize}
        loading={listState.loading}
        error={listState.error}
        filters={filters}
        selectedId={selectedId}
        onFilters={handleFilters}
        onPage={setPage}
        onSelect={handleSelect}
        onSync={handleSync}
        syncing={busy === "sync"}
        onRetry={(target) => target?.id ? handleRetry(target) : loadList()}
        onRating={handleRating}
        selectedRows={selectedRows}
        onToggleRow={(id) => setSelectedRows((current) => {
          const next = new Set(current);
          if (next.has(Number(id))) next.delete(Number(id)); else next.add(Number(id));
          return next;
        })}
        onTogglePage={() => setSelectedRows((current) => (
          current.size === list.items.length
            ? new Set()
            : new Set(list.items.map((item) => Number(item.id)))
        ))}
        onBulkProcess={handleBulkProcess}
        bulkBusy={busy === "bulk"}
        onPrefetch={scheduleHoverPrefetch}
        onCancelPrefetch={cancelHoverPrefetch}
      />
      <div
        className="inquiry-resizer"
        role="separator"
        aria-label="调整询盘列表宽度"
        aria-orientation="vertical"
        aria-valuemin="280"
        aria-valuemax="620"
        aria-valuenow={Math.round(listWidth)}
        tabIndex={0}
        onMouseDown={startResize}
        onKeyDown={(event) => {
          if (event.key === "ArrowLeft") { event.preventDefault(); resizeListBy(-20); }
          if (event.key === "ArrowRight") { event.preventDefault(); resizeListBy(20); }
        }}
      />
      <InquiryCenterPane
        inquiry={displayedInquiry}
        context={context}
        researchDetail={secondary}
        secondaryLoading={secondaryState.loading}
        secondaryError={secondaryState.error}
        loading={detailState.loading}
        error={detailState.error}
        onRetry={() => loadDetail()}
        onOriginal={() => setModals((value) => ({ ...value, original: true }))}
        onRating={(rating) => handleRating(displayedInquiry, rating)}
        onOpenRating={() => setRatingEditor({ open: true, target: displayedInquiry, selectedRating: 0 })}
        onOpenHistory={handleOpenHistory}
        onSaveFollowUp={(payload) => handleContext(() => api.updateCustomerFollowUp(inquiry.id, payload), "跟进计划已保存")}
        onAddNote={(note) => handleContext(() => api.addCustomerNote(inquiry.id, note), "内部备注已添加")}
        contextBusy={contextBusy}
        onTranslate={handleTranslate}
        translating={busy === "translate"}
        bodyLoading={bodyContentState.id === Number(displayedInquiry?.id) && bodyContentState.loading}
        bodyError={bodyContentState.id === Number(displayedInquiry?.id) ? bodyContentState.error : ""}
        onBodyRetry={() => setBodyRetryKey((value) => value + 1)}
        onMobileBack={() => setMobileView("list")}
        activeTab={detailTab}
        onTab={setDetailTab}
        settings={settings}
        suggestions={suggestions}
        busy={busy}
        onResearch={handleResearch}
        onOpenTemplates={() => setModals((value) => ({ ...value, prompts: true }))}
        onGenerate={handleGenerate}
        onSaveDraft={handleSaveDraft}
        onRecipientsSave={handleRecipients}
        onOpenSend={handleOpenSend}
        onPreview={handlePreview}
        onAutoProcess={() => handleAutoProcess(inquiry)}
        aiProgress={aiProgress}
      />
      {displayedInquiry ? (
        <>
          <OriginalEmailModal inquiry={displayedInquiry} open={modals.original} onClose={() => setModals((value) => ({ ...value, original: false }))} notify={notify} onSaved={updateSelected} />
          <PromptTemplatesModal open={modals.prompts} onClose={() => setModals((value) => ({ ...value, prompts: false }))} notify={notify} />
          <ReplyPreviewModal open={modals.preview} onClose={() => setModals((value) => ({ ...value, preview: false }))} inquiry={displayedInquiry} settings={settings} payload={sendPayload} />
          <SendConfirm open={modals.send} onClose={() => setModals((value) => ({ ...value, send: false }))} inquiry={displayedInquiry} payload={sendPayload} onConfirm={confirmSend} loading={busy === "send"} />
          <RatingDetailModal open={ratingEditor.open} inquiry={ratingEditor.target?.id === displayedInquiry?.id ? displayedInquiry : ratingEditor.target || displayedInquiry} selectedRating={ratingEditor.selectedRating} loading={busy === "rating"} onClose={() => setRatingEditor({ open: false, target: null, selectedRating: 0 })} onSave={saveRating} onRestore={restoreSystemRating} />
        </>
      ) : null}
    </div>
  );
}
